import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { Calculator as CalcIcon, Ruler, Clock, Euro, Wrench, FileText } from "lucide-react";

const euro = (n) => (n || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR" });

const PRODUCTS = [
  { key: "bache", label: "Bâche de protection" },
  { key: "coussin", label: "Coussin / assise" },
  { key: "housse", label: "Housse sur mesure" },
  { key: "capote", label: "Capote / taud" },
];

export default function Calculator() {
  const navigate = useNavigate();
  const [materials, setMaterials] = useState([]);
  const [form, setForm] = useState({
    product_type: "bache", length_cm: 300, width_cm: 200, height_cm: 0,
    quantity: 1, material: "pvc_680", eyelets: 12, zipper_ml: 0, hem_cm: 5, labor_rate: 45,
  });
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/materials").then((r) => setMaterials(r.data)).catch(() => {});
  }, []);

  const set = (k, isNum = true) => (e) =>
    setForm({ ...form, [k]: isNum ? parseFloat(e.target.value) || 0 : e.target.value });

  const compute = async () => {
    const { data } = await api.post("/materials/calculate", form);
    setResult(data);
    api.post("/calculations", { linear_m: data.linear_m, material: form.material }).catch(() => {});
  };

  const toQuote = async () => {
    if (!result) return;
    const { data } = await api.post("/documents", {
      type: "devis", client_name: "", title: `${PRODUCTS.find(p => p.key === form.product_type)?.label} sur mesure`,
      items: [
        { description: `${result.material_label} — ${result.area_m2} m²`, quantity: 1, unit: "u", unit_price: result.material_cost, vat_rate: 20 },
        { description: `Main d'œuvre (${result.labor_hours} h)`, quantity: 1, unit: "u", unit_price: result.labor_cost, vat_rate: 20 },
        { description: "Accessoires (œillets, fermetures…)", quantity: 1, unit: "u", unit_price: result.accessories_cost, vat_rate: 20 },
      ],
    });
    toast.success("Devis pré-rempli créé");
    navigate(`/documents/${data.id}`);
  };

  const Field = ({ label, k, unit }) => (
    <div>
      <label className="text-xs font-medium text-slate-500">{label}</label>
      <div className="relative">
        <input type="number" value={form[k]} onChange={set(k)} data-testid={`calc-${k}`}
          className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-sky-400" />
        {unit && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">{unit}</span>}
      </div>
    </div>
  );

  return (
    <div className="space-y-6 animate-fade-up">
      <div>
        <h1 className="text-3xl font-display font-bold text-navy">Calculateur Matières & Temps</h1>
        <p className="text-slate-500 mt-1">Estimez la matière, la main d'œuvre et le prix d'une pièce sur mesure.</p>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 bg-white rounded-2xl border border-slate-200 p-6">
          <div className="flex items-center gap-2 text-navy font-display font-semibold mb-4">
            <CalcIcon className="w-5 h-5 text-sky-500" /> Paramètres
          </div>

          <label className="text-xs font-medium text-slate-500">Type de pièce</label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-1 mb-4">
            {PRODUCTS.map((p) => (
              <button key={p.key} onClick={() => setForm({ ...form, product_type: p.key })}
                data-testid={`product-${p.key}`}
                className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                  form.product_type === p.key ? "bg-navy text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}>
                {p.label}
              </button>
            ))}
          </div>

          <label className="text-xs font-medium text-slate-500">Matière</label>
          <select value={form.material} onChange={set("material", false)} data-testid="calc-material"
            className="mt-1 mb-4 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-sky-400">
            {materials.map((m) => (
              <option key={m.key} value={m.key}>{m.label} — {m.price_per_m2} €/m² (laize {m.roll_width_cm}cm)</option>
            ))}
          </select>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <Field label="Longueur" k="length_cm" unit="cm" />
            <Field label="Largeur" k="width_cm" unit="cm" />
            <Field label="Hauteur" k="height_cm" unit="cm" />
            <Field label="Quantité" k="quantity" unit="u" />
            <Field label="Ourlet" k="hem_cm" unit="cm" />
            <Field label="Œillets" k="eyelets" unit="u" />
            <Field label="Fermeture" k="zipper_ml" unit="ml" />
            <Field label="Taux horaire" k="labor_rate" unit="€/h" />
          </div>

          <button onClick={compute} data-testid="calc-submit-btn"
            className="mt-5 w-full py-3 rounded-xl bg-sky-400 text-navy font-semibold hover:bg-sky-500 transition-colors">
            Calculer
          </button>
        </div>

        <div className="lg:col-span-2 space-y-4">
          {result ? (
            <>
              <div className="bg-navy rounded-2xl p-6 text-white" data-testid="calc-result">
                <div className="text-sm text-slate-300">Prix de revient estimé</div>
                <div className="text-4xl font-display font-bold mt-1 tabular tracking-tight">{euro(result.total_cost)}</div>
                <div className="mt-2 text-sm text-sky-300">Prix de vente conseillé : <span className="font-mono font-semibold">{euro(result.suggested_price_ht)}</span> HT</div>
              </div>

              <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
                {[
                  { icon: Ruler, label: "Métrage linéaire", value: `${result.linear_m} m`, sub: `${result.area_m2} m² · laize ${result.roll_width_cm}cm` },
                  { icon: Euro, label: "Matière", value: euro(result.material_cost) },
                  { icon: Clock, label: "Main d'œuvre", value: `${result.labor_hours} h · ${euro(result.labor_cost)}` },
                  { icon: Wrench, label: "Accessoires", value: euro(result.accessories_cost) },
                ].map((r, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-sky-50 flex items-center justify-center">
                      <r.icon className="w-4 h-4 text-sky-600" />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-navy font-mono">{r.value}</div>
                      <div className="text-xs text-slate-500">{r.label}{r.sub ? ` · ${r.sub}` : ""}</div>
                    </div>
                  </div>
                ))}
              </div>

              <button onClick={toQuote} data-testid="calc-to-quote-btn"
                className="w-full py-3 rounded-xl bg-navy text-white font-semibold hover:bg-navy-deep transition-colors inline-flex items-center justify-center gap-2">
                <FileText className="w-4 h-4" /> Générer un devis
              </button>
            </>
          ) : (
            <div className="bg-white rounded-2xl border border-dashed border-slate-200 p-10 text-center text-slate-400">
              <CalcIcon className="w-8 h-8 mx-auto mb-2 opacity-40" />
              Renseignez les paramètres puis lancez le calcul.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
